import { useState, useEffect, useRef, useCallback } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Calendar } from "@/components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { toast } from "sonner";
import { CameraScannerDialog } from "@/components/CameraScanner";
import { ScanStation } from "@/components/ScanStation";
import { CarrierShipments } from "@/components/CarrierShipments";
import { playScanSound } from "@/components/scanSound";
import { 
  ScanLine, 
  Package, 
  CheckCircle2, 
  Truck, 
  Clock,
  Volume2,
  VolumeX,
  History,
  Trash2,
  Zap,
  MapPin,
  Music,
  Search,
  CalendarIcon,
  Plus,
  X,
  ChevronLeft,
  ChevronRight,
  User,
  Camera
} from "lucide-react";
import axios from "axios";
import { format, subDays, addDays } from "date-fns";
import { it } from "date-fns/locale";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Scanner = () => {
  // Scanner state
  const [scannedCode, setScannedCode] = useState("");
  const [isProcessing, setIsProcessing] = useState(false);
  const [soundEnabled, setSoundEnabled] = useState(true);
  const [isReady, setIsReady] = useState(true);
  const [selectedCarrier, setSelectedCarrier] = useState("NAPOLI");
  const inputRef = useRef(null);
  const [cameraOpen, setCameraOpen] = useState(false);
  const [sessionScans, setSessionScans] = useState([]);
  const [pendingUnmatched, setPendingUnmatched] = useState(null);
  const [manualOpen, setManualOpen] = useState(false);
  const lastKeyTime = useRef(Date.now());
  
  // Shipments state
  const [shipments, setShipments] = useState([]);
  const [stats, setStats] = useState({ NAPOLI: 0, GLS: 0, TIKTOK: 0, total: 0 });
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [searchQuery, setSearchQuery] = useState("");
  const [activeTab, setActiveTab] = useState("scanner");
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  
  // Manual add state
  const [manualCode, setManualCode] = useState("");
  const [manualName, setManualName] = useState("");
  const [manualNotes, setManualNotes] = useState("");
  
  // Get auth token
  const getToken = () => localStorage.getItem("token");
  
  // Audio feedback (loud, instant)
  const playSound = useCallback((type) => {
    if (soundEnabled) playScanSound(type);
  }, [soundEnabled]);
  
  // Load shipments for selected date
  const loadShipments = useCallback(async (carrier = null) => {
    try {
      const dateStr = format(selectedDate, "yyyy-MM-dd");
      let url = `${API_URL}/api/shipments?date=${dateStr}`;
      if (carrier) url += `&carrier=${carrier}`;
      if (searchQuery) url += `&search=${encodeURIComponent(searchQuery)}`;
      
      const res = await axios.get(url, {
        headers: { Authorization: `Bearer ${getToken()}` }
      });
      
      setShipments(res.data.shipments || []);
    } catch (error) {
      console.error("Error loading shipments:", error);
    }
  }, [selectedDate, searchQuery]);
  
  // Load stats for selected date
  const loadStats = useCallback(async () => {
    try {
      const dateStr = format(selectedDate, "yyyy-MM-dd");
      const res = await axios.get(`${API_URL}/api/shipments/stats?date=${dateStr}`, {
        headers: { Authorization: `Bearer ${getToken()}` }
      });
      
      setStats(res.data);
    } catch (error) {
      console.error("Error loading stats:", error);
    }
  }, [selectedDate]);

  // Today's total from every device (two scanners at once): polled every 3 seconds
  const [todayStats, setTodayStats] = useState({ total: 0 });
  const saveSeq = useRef(0);
  const loadTodayStats = useCallback(async () => {
    const seq = saveSeq.current;
    try {
      const res = await axios.get(`${API_URL}/api/shipments/stats`, { headers: { Authorization: `Bearer ${getToken()}` } });
      if (seq === saveSeq.current) setTodayStats(res.data);
    } catch { /* retry on next poll */ }
  }, []);
  useEffect(() => {
    loadTodayStats();
    const t = setInterval(loadTodayStats, 3000);
    return () => clearInterval(t);
  }, [loadTodayStats]);
  
  // Load data when date changes
  useEffect(() => {
    loadStats();
    if (activeTab !== "scanner") {
      loadShipments(activeTab.toUpperCase());
    }
  }, [selectedDate, loadStats, loadShipments, activeTab]);
  
  // Focus management for scanner
  const maintainFocus = useCallback(() => {
    if (activeTab === "scanner" && !cameraOpen && !manualOpen && inputRef.current && document.activeElement !== inputRef.current) {
      inputRef.current.focus();
    }
  }, [activeTab, cameraOpen, manualOpen]);
  
  useEffect(() => {
    if (activeTab === "scanner") {
      maintainFocus();
      const interval = setInterval(maintainFocus, 2000);
      return () => clearInterval(interval);
    }
  }, [activeTab, maintainFocus]);
  
  // Handle barcode scan
  const refreshTimer = useRef(null);
  const scheduleRefresh = useCallback(() => {
    clearTimeout(refreshTimer.current);
    refreshTimer.current = setTimeout(() => loadStats(), 400);
  }, [loadStats]);

  const updateScan = (key, patch) => setSessionScans(prev => prev.map(s => (s.key === key ? { ...s, ...patch } : s)));

  // Optimistic scan: beep and show the row instantly, save in background, so the next code can be shot right away
  const handleScan = useCallback(async (code, force = false) => {
    const cleanCode = (code || "").toUpperCase().trim();
    setScannedCode("");
    if (cleanCode.length < 5) return;

    let carrier = selectedCarrier;
    if (/^NAP\d{8}$/.test(cleanCode)) carrier = 'NAPOLI';
    else if (/^G\d{8}$/.test(cleanCode) || /^NN\d{6,}$/.test(cleanCode)) carrier = 'GLS';

    const key = `scan-${Date.now()}-${Math.random()}`;
    const time = new Date().toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit", second: "2-digit" });
    playSound('success');
    if (navigator.vibrate) navigator.vibrate(60);
    setSessionScans(prev => [{ key, code: cleanCode, carrier, pending: true, time }, ...prev]);

    try {
      const res = await axios.post(`${API_URL}/api/shipments`, { code: cleanCode, carrier, force },
        { headers: { Authorization: `Bearer ${getToken()}` } });
      updateScan(key, { pending: false, carrier: res.data.carrier || carrier, customer_name: res.data.customer_name, order_id: res.data.order_id, tracking: res.data.tracking, id: res.data.id });
      saveSeq.current += 1;
      setTodayStats(s => ({ ...s, total: (s.total || 0) + 1 }));
      scheduleRefresh();
    } catch (error) {
      const detail = error.response?.data?.detail;
      if (error.response?.status === 409 && detail) {
        playSound('duplicate');
        if (navigator.vibrate) navigator.vibrate([300, 100, 300, 100, 300]);
        const first = detail.scanned_at ? new Date(detail.scanned_at).toLocaleString("it-IT", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" }) : "";
        const other = detail.first_code && detail.first_code !== cleanCode ? ` con l'altra etichetta ${detail.first_code}` : "";
        updateScan(key, { pending: false, carrier: detail.carrier || carrier, customer_name: detail.customer_name, duplicate: true, error: `GIÀ SCANSIONATO${other}${first ? ` il ${first}` : ""}` });
        return;
      }
      if (error.response?.status === 404 && detail?.unmatched) {
        playSound('error');
        if (navigator.vibrate) navigator.vibrate([200, 100, 200]);
        setSessionScans(prev => prev.filter(s => s.key !== key));
        setPendingUnmatched(cleanCode);
        return;
      }
      playSound('error');
      updateScan(key, { pending: false, error: typeof detail === "string" ? detail : "Non salvato: rispara il codice" });
    }
  }, [selectedCarrier, playSound, scheduleRefresh]); // eslint-disable-line react-hooks/exhaustive-deps
  
  // Handle keyboard input
  const handleKeyDown = useCallback((e) => {
    if (e.key === 'Enter' && scannedCode.length >= 5) {
      e.preventDefault();
      clearTimeout(burstRef.current.timer);
      handleScan(scannedCode);
    }
  }, [scannedCode, handleScan]);
  
  // Handle input change: scanners type a burst of characters, so auto-submit when the burst stops
  const burstRef = useRef({ start: 0, timer: null });
  const handleInputChange = useCallback((e) => {
    const value = e.target.value;
    setScannedCode(value);
    const b = burstRef.current;
    const now = Date.now();
    if (value.length <= 1) b.start = now;
    clearTimeout(b.timer);
    const fastTyping = value.length >= 6 && (now - b.start) / (value.length - 1) < 35;
    if (fastTyping) {
      b.timer = setTimeout(() => handleScan(value), 70);
    }
  }, [handleScan]);
  
  // Delete shipment
  const handleDeleteShipment = async (shipmentId) => {
    if (!confirm("Rimuovere questa spedizione?")) return;
    
    try {
      await axios.delete(`${API_URL}/api/shipments/${shipmentId}`, {
        headers: { Authorization: `Bearer ${getToken()}` }
      });
      
      toast.success("Spedizione rimossa");
      loadShipments(activeTab.toUpperCase());
      loadStats();
    } catch (error) {
      toast.error("Errore nella rimozione");
    }
  };
  
  // Add manual shipment
  const handleAddManual = async () => {
    if (!manualCode) {
      toast.error("Inserisci un codice");
      return;
    }
    
    try {
      const formData = new FormData();
      formData.append('code', manualCode);
      formData.append('carrier', selectedCarrier);
      if (manualName) formData.append('customer_name', manualName);
      if (manualNotes) formData.append('notes', manualNotes);
      
      await axios.post(`${API_URL}/api/shipments/manual`, formData, {
        headers: { Authorization: `Bearer ${getToken()}` }
      });
      
      toast.success("Spedizione aggiunta manualmente");
      setIsAddDialogOpen(false);
      setManualCode("");
      setManualName("");
      setManualNotes("");
      loadStats();
      
    } catch (error) {
      toast.error("Errore nell'aggiunta");
    }
  };
  
  // Navigate date
  const goToPreviousDay = () => setSelectedDate(prev => subDays(prev, 1));
  const goToNextDay = () => {
    const tomorrow = addDays(selectedDate, 1);
    if (tomorrow <= new Date()) {
      setSelectedDate(tomorrow);
    }
  };
  const goToToday = () => setSelectedDate(new Date());
  
  // Filter shipments by search
  const filteredShipments = shipments.filter(s => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return s.code?.toLowerCase().includes(query) || 
           s.customer_name?.toLowerCase().includes(query);
  });
  
  // Get carrier color
  const getCarrierColor = (carrier) => {
    switch (carrier) {
      case 'NAPOLI': return 'bg-violet-500';
      case 'GLS': return 'bg-amber-500';
      case 'TIKTOK': return 'bg-black';
      default: return 'bg-gray-500';
    }
  };
  
  const getCarrierBorderColor = (carrier) => {
    switch (carrier) {
      case 'NAPOLI': return 'border-l-violet-500';
      case 'GLS': return 'border-l-amber-500';
      case 'TIKTOK': return 'border-l-black';
      default: return 'border-l-gray-500';
    }
  };

  return (
    <div className="min-h-screen bg-slate-100">
      {/* Header */}
      <div className="bg-white border-b border-slate-200 px-4 py-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-violet-600 rounded-xl flex items-center justify-center">
              <ScanLine className="w-5 h-5 text-white" />
            </div>
            <div>
              <h1 className="text-lg font-bold text-slate-800">Scanner e Spedizioni</h1>
              <p className="text-xs text-slate-500">Traccia le spedizioni in tempo reale</p>
            </div>
          </div>
          
          {/* Date Navigation */}
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="icon" onClick={goToPreviousDay}>
              <ChevronLeft className="w-4 h-4" />
            </Button>
            
            <Popover>
              <PopoverTrigger asChild>
                <Button variant="outline" size="sm" className="min-w-[140px]">
                  <CalendarIcon className="w-4 h-4 mr-2" />
                  {format(selectedDate, "dd MMM yyyy", { locale: it })}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="end">
                <Calendar
                  mode="single"
                  selected={selectedDate}
                  onSelect={(date) => date && setSelectedDate(date)}
                  disabled={(date) => date > new Date()}
                  initialFocus
                />
              </PopoverContent>
            </Popover>
            
            <Button variant="ghost" size="icon" onClick={goToNextDay} disabled={format(selectedDate, "yyyy-MM-dd") === format(new Date(), "yyyy-MM-dd")}>
              <ChevronRight className="w-4 h-4" />
            </Button>
            
            {format(selectedDate, "yyyy-MM-dd") !== format(new Date(), "yyyy-MM-dd") && (
              <Button variant="outline" size="sm" onClick={goToToday}>
                Oggi
              </Button>
            )}
          </div>
        </div>
      </div>
      
      {/* Stats Bar */}
      <div className="bg-white border-b border-slate-200 px-4 py-3">
        <div className="flex flex-wrap items-center gap-2 sm:gap-4">
          <div className="flex items-center gap-2 px-3 py-2 bg-slate-100 rounded-lg">
            <Package className="w-4 h-4 text-slate-600" />
            <span className="font-bold text-slate-800">{stats.total}</span>
            <span className="text-xs text-slate-500">Totale</span>
          </div>
          
          <div className="flex items-center gap-2 px-3 py-2 bg-violet-100 rounded-lg">
            <MapPin className="w-4 h-4 text-violet-600" />
            <span className="font-bold text-violet-700">{stats.NAPOLI}</span>
            <span className="text-xs text-violet-600">Napoli</span>
          </div>
          
          <div className="flex items-center gap-2 px-3 py-2 bg-amber-100 rounded-lg">
            <Truck className="w-4 h-4 text-amber-600" />
            <span className="font-bold text-amber-700">{stats.GLS}</span>
            <span className="text-xs text-amber-600">GLS</span>
          </div>
          
          <div className="flex items-center gap-2 px-3 py-2 bg-slate-800 rounded-lg">
            <Music className="w-4 h-4 text-white" />
            <span className="font-bold text-white">{stats.TIKTOK}</span>
            <span className="text-xs text-slate-300">TikTok</span>
          </div>
        </div>
      </div>
      
      {/* Main Content with Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="flex flex-col h-[calc(100vh-180px)]">
        {/* Tab Content */}
        {/* Carrier tabs (top, away from floating badges) */}
        <TabsList className="grid grid-cols-4 h-16 bg-white border-b border-slate-200 rounded-none" data-testid="scanner-tabbar">
          <TabsTrigger value="scanner" className="flex flex-col gap-1 data-[state=active]:bg-violet-100">
            <ScanLine className="w-5 h-5" />
            <span className="text-xs">Scanner</span>
          </TabsTrigger>
          <TabsTrigger value="napoli" className="flex flex-col gap-1 data-[state=active]:bg-violet-100">
            <MapPin className="w-5 h-5" />
            <span className="text-xs">Napoli</span>
          </TabsTrigger>
          <TabsTrigger value="gls" className="flex flex-col gap-1 data-[state=active]:bg-amber-100">
            <Truck className="w-5 h-5" />
            <span className="text-xs">GLS</span>
          </TabsTrigger>
          <TabsTrigger value="tiktok" className="flex flex-col gap-1 data-[state=active]:bg-slate-200">
            <Music className="w-5 h-5" />
            <span className="text-xs">TikTok</span>
          </TabsTrigger>
        </TabsList>
        <div className="flex-1 overflow-auto p-4">
          {/* Scanner Tab */}
          <TabsContent value="scanner" className="m-0 h-full">
            <div className="max-w-lg mx-auto space-y-4">
              <ScanStation
                inputProps={{ ref: inputRef, value: scannedCode, onChange: handleInputChange, onKeyDown: handleKeyDown, onBlur: maintainFocus }}
                carrier={selectedCarrier}
                setCarrier={setSelectedCarrier}
                isReady={true}
                scans={sessionScans}
                todayTotal={todayStats.total || 0}
                onReset={() => setSessionScans([])}
                onCamera={() => { setCameraOpen(true); setTimeout(() => inputRef.current?.blur(), 0); }}
                onManual={() => setIsAddDialogOpen(true)}
                soundEnabled={soundEnabled}
                setSoundEnabled={setSoundEnabled}
                pendingUnmatched={pendingUnmatched}
                onConfirmUnmatched={(save) => { const c = pendingUnmatched; setPendingUnmatched(null); if (save) handleScan(c, true); else setTimeout(maintainFocus, 50); }}
                manualOpen={manualOpen}
                setManualOpen={setManualOpen}
                onManualSubmit={(c) => handleScan(c)}
              />
              <CameraScannerDialog open={cameraOpen} onClose={() => setCameraOpen(false)} onCode={handleScan} />
            </div>
          </TabsContent>
          
          {/* Carrier Tabs */}
          {['napoli', 'gls', 'tiktok'].map(carrier => (
            <TabsContent key={carrier} value={carrier} className="m-0 h-full">
              <CarrierShipments
                carrier={carrier.toUpperCase()}
                shipments={filteredShipments}
                count={stats[carrier.toUpperCase()] || 0}
                date={selectedDate}
                searchQuery={searchQuery}
                setSearchQuery={setSearchQuery}
                onReload={() => { loadShipments(carrier.toUpperCase()); loadStats(); loadTodayStats(); }}
                onDelete={handleDeleteShipment}
                breakdown={stats.tiktok_breakdown}
              />
            </TabsContent>
          ))}
        </div>
        
      </Tabs>
      
      {/* Manual Add Dialog */}
      <Dialog open={isAddDialogOpen} onOpenChange={setIsAddDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Aggiungi Spedizione Manuale</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Corriere</Label>
              <div className="flex gap-2">
                {['NAPOLI', 'GLS', 'TIKTOK'].map(carrier => (
                  <Button
                    key={carrier}
                    variant={selectedCarrier === carrier ? 'default' : 'outline'}
                    size="sm"
                    className={`flex-1 ${selectedCarrier === carrier ? getCarrierColor(carrier) : ''}`}
                    onClick={() => setSelectedCarrier(carrier)}
                  >
                    {carrier}
                  </Button>
                ))}
              </div>
            </div>
            
            <div className="space-y-2">
              <Label>Codice Spedizione *</Label>
              <Input
                placeholder={selectedCarrier === 'NAPOLI' ? 'NAP10090001' : selectedCarrier === 'GLS' ? 'G10090001' : 'Codice...'}
                value={manualCode}
                onChange={(e) => setManualCode(e.target.value)}
              />
            </div>
            
            <div className="space-y-2">
              <Label>Nome Destinatario (opzionale)</Label>
              <Input
                placeholder="Mario Rossi"
                value={manualName}
                onChange={(e) => setManualName(e.target.value)}
              />
            </div>
            
            <div className="space-y-2">
              <Label>Note (opzionale)</Label>
              <Input
                placeholder="Note aggiuntive..."
                value={manualNotes}
                onChange={(e) => setManualNotes(e.target.value)}
              />
            </div>
            
            <div className="flex justify-end gap-2 pt-4">
              <Button variant="outline" onClick={() => setIsAddDialogOpen(false)}>
                Annulla
              </Button>
              <Button onClick={handleAddManual} className={getCarrierColor(selectedCarrier)}>
                <Plus className="w-4 h-4 mr-1" />
                Aggiungi
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Scanner;
